/* ============================================================
   Kaelder FBT drawer helpers (adds a merged FBT widget to the cart drawer)
   - Requires a section: sections/cart-kaelder-fbt.liquid (id: cart-kaelder-fbt)
   - Optionally add <div data-kaelder-fbt-container></div> in drawer markup.
   - If no container exists, we auto-insert one above the footer.
   ============================================================ */
const KAELDER_SECTION_ID = 'cart-kaelder-fbt';

/* Build a locale/market-aware section URL so Liquid renders in the active Market/locale */
function __kaelderSectionURL(sectionId) {
  const rootCart =
    (window.MinimogSettings && MinimogSettings.routes && MinimogSettings.routes.cart)
      ? MinimogSettings.routes.cart                 // e.g. "/de/cart"
      : (window.Shopify && Shopify.routes && Shopify.routes.root)
        ? (Shopify.routes.root + 'cart')            // e.g. "/de/" + "cart"
        : '/cart';

  const url = new URL(rootCart, window.location.origin);
  url.searchParams.set('section_id', sectionId);

  // Be explicit about locale to force correct translations
  const locale =
    (document.documentElement && document.documentElement.lang) ||
    (window.Shopify && Shopify.locale) ||
    '';
  if (locale) url.searchParams.set('locale', locale);

  return url.toString();
}

function __kaelderEnsureSlot(drawerEl) {
  let slot = drawerEl.querySelector('[data-kaelder-fbt-container]');
  if (!slot) {
    slot = document.createElement('div');
    slot.setAttribute('data-kaelder-fbt-container', '');
    // Place above footer; tweak selectors if your drawer differs
    const footer =
      drawerEl.querySelector('.cart-drawer__footer') ||
      drawerEl.querySelector('[data-minimog-cart-discounts]') ||
      drawerEl;
    footer.parentNode.insertBefore(slot, footer);
  }
  return slot;
}

/* Watch the cart items DOM to catch remove/qty changes even if no event fires */
function __kaelderObserveCartItems(drawerEl) {
  const list = drawerEl.querySelector('[data-minimog-cart-items]');
  if (!list || list.__kaelderObserved) return;
  list.__kaelderObserved = true;
  const mo = new MutationObserver(() => __kaelderRenderFBT(drawerEl));
  mo.observe(list, { childList: true, subtree: false });
}

/* Subscribe to Minimog cart events (cartUpdated, itemRemoved, etc.) */
function __kaelderHookCartEvents() {
  const drawerEl = document.querySelector('m-cart-drawer');
  if (!window.MinimogEvents || !window.MinimogTheme || !drawerEl) return;
  const ev = window.MinimogTheme.pubSubEvents;
  Object.keys(ev).forEach((k) => {
    if (/cart/i.test(k)) {
      window.MinimogEvents.on(ev[k], () => __kaelderRenderFBT(drawerEl));
    }
  });
}

async function __kaelderRenderFBT(drawerEl) {
  try {
    const slot = __kaelderEnsureSlot(drawerEl);

    // Fetch the section with current cart + locale/market context
    const res = await fetch(__kaelderSectionURL(KAELDER_SECTION_ID), { credentials: 'same-origin' });
    const html = await res.text();
    if (!html) return;

    // Inject HTML
    const doc = new DOMParser().parseFromString(html, 'text/html');
    slot.innerHTML = doc.body.innerHTML;

    // Wire up add-to-cart buttons (per-item properties only)
    slot.querySelectorAll('.fbt-add-to-cart').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = btn.getAttribute('data-variant-id');
        if (!variantId) return;

        const prev = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';

        try {
          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              id: variantId,
              quantity: 1,
              properties: {
                _kaelder: 'true',
                _kaelder_widget: 'drawer-fbt'
              }
            })
          });
          if (!addRes.ok) throw new Error('Add failed');

          // Ask Minimog to refresh the drawer, then re-render our FBT section
          if (drawerEl && typeof drawerEl.onCartDrawerUpdate === 'function') {
            await drawerEl.onCartDrawerUpdate();
          }
          await __kaelderRenderFBT(drawerEl);
        } catch (err) {
          console.error('Kaelder add failed', err);
          alert('Could not add item. Please try again.');
        } finally {
          btn.disabled = false;
          btn.textContent = prev;
        }
      });
    });

    // Optional: scroller controls if you ever render a horizontal grid
    const grid = slot.querySelector('.fbt-grid');
    const prevBtn = slot.querySelector('.fbt-nav--prev');
    const nextBtn = slot.querySelector('.fbt-nav--next');
    if (grid && prevBtn && nextBtn) {
      const gap = () => parseFloat(getComputedStyle(grid).gap) || 0;
      const itemW = () => {
        const card = grid.querySelector('.fbt-item');
        return card ? Math.ceil(card.getBoundingClientRect().width + gap()) : 220;
      };
      const snap = (d = 1) => grid.scrollBy({ left: d * itemW(), behavior: 'smooth' });
      const update = () => {
        const max = grid.scrollWidth - grid.clientWidth - 1;
        prevBtn.disabled = grid.scrollLeft <= 1;
        nextBtn.disabled = grid.scrollLeft >= max;
      };
      prevBtn.addEventListener('click', () => snap(-1));
      nextBtn.addEventListener('click', () => snap(1));
      grid.addEventListener('scroll', update, { passive: true });
      requestAnimationFrame(update);
    }

    // Observe list changes (remove/qty)
    __kaelderObserveCartItems(drawerEl);
  } catch (e) {
    console.error('Kaelder FBT render failed', e);
  }
}

/* ============================================================
   Minimog cart drawer (patched with Kaelder refresh calls)
   ============================================================ */

class MCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.cartDrawerInner = this.querySelector('.m-cart-drawer__inner');
    this.cartDrawerCloseIcon = this.querySelector('.m-cart-drawer__close');
    this.cartOverlay = this.querySelector('.m-cart__overlay');
    this.rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    this.setHeaderCartIconAccessibility();

    if (this.cartDrawerCloseIcon) {
      this.cartDrawerCloseIcon.addEventListener('click', this.close.bind(this));
    }
    this.addEventListener('click', (evt) => {
      if (evt.target.closest('.m-cart-drawer__inner') !== this.cartDrawerInner) this.close();
    });
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll('.m-cart-icon-bubble').forEach((el) => {
      el.setAttribute('role', 'button');
      el.setAttribute('aria-haspopup', 'dialog');
      el.addEventListener('click', (e) => {
        if (window.MinimogSettings && MinimogSettings.enable_cart_drawer) {
          e.preventDefault();
          this.open(el);
        }
      });
    });
  }

  open(trigger) {
    if (trigger) this.setActiveElement(trigger);
    this.classList.add('m-cart-drawer--active');
    requestAnimationFrame(() => {
      this.style.setProperty('--m-bg-opacity', '0.5');
      this.cartDrawerInner && this.cartDrawerInner.style.setProperty('--translate-x', '0');
    });
    if (window.MinimogEvents && window.MinimogTheme) {
      window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.openCartDrawer);
    }

    // Kaelder: render merged FBT when the drawer opens (locale-aware)
    __kaelderRenderFBT(this);
  }

  close() {
    this.style.setProperty('--m-bg-opacity', '0');
    this.cartDrawerInner && this.cartDrawerInner.style.setProperty('--translate-x', '100%');
    setTimeout(() => {
      this.classList.remove('m-cart-drawer--active');
    }, 300);
  }

  renderContents(payload) {
    if (this.classList.contains('m-cart--empty')) {
      this.classList.remove('m-cart--empty');
    }

    this.productId = payload.id;

    this.getSectionsToRender().forEach((s) => {
      const target = s.selector ? document.querySelector(s.selector) : document.getElementById(s.id);
      if (!target) return;
      target.innerHTML = this.getSectionInnerHTML(payload.sections[s.id], s.selector);
    });

    setTimeout(() => {
      this.open();
      if (window.FoxKitV2 && window.FoxKitV2.Modules && window.FoxKitV2.Modules.InCart) {
        window.FoxKitV2.Modules.InCart.getCart();
      }
      // Kaelder: after full drawer render (often on remove/qty), refresh FBT
      __kaelderRenderFBT(this);
    });
  }

  updateCartCount(n) {
    document.querySelectorAll('.m-cart-count-bubble').forEach((el) => {
      if (n > 0) {
        el.textContent = n;
        el.classList.remove('hidden');
      } else {
        el.classList.add('hidden');
      }
    });
  }

  getCart() {
    if (typeof fetchJSON === 'function') return fetchJSON(this.rootUrl + 'cart.json');
    // Fallback if theme doesn’t expose fetchJSON
    return fetch(this.rootUrl + 'cart.json', { credentials: 'same-origin' }).then((r) => r.json());
  }

  onCartDrawerUpdate(force = true) {
    // Fetch the whole cart-drawer section in the active locale/market
    fetch(__kaelderSectionURL('cart-drawer'))
      .then((r) => r.text())
      .then((html) => {
        this.getSectionsToRender().forEach((s) => {
          const target = s.selector ? document.querySelector(s.selector) : document.getElementById(s.id);
          if (!target) return;
          target.innerHTML = this.getSectionInnerHTML(html, s.selector);
          if (s.block === 'cart-items' && window.FoxKitV2 && window.FoxKitV2.Modules && window.FoxKitV2.Modules.InCart) {
            window.FoxKitV2.Modules.InCart.getCart();
          }
        });

        // Kaelder: re-render FBT after the drawer’s content updates
        __kaelderRenderFBT(this);
      })
      .catch(() => {})
      .finally(() => {
        this.getCart().then((data) => {
          this.classList.toggle('m-cart--empty', data.item_count === 0);
          this.updateCartCount(data.item_count);
        });
      });
  }

  getSectionInnerHTML(source, selector = '.shopify-section') {
    return new DOMParser().parseFromString(source, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '[data-shipping]' },
      { id: 'cart-drawer', selector: '[data-minimog-cart-items]', block: 'cart-items' },
      { id: 'cart-drawer', selector: '[data-minimog-cart-discounts]', block: 'cart-footer' },
      { id: 'cart-drawer', selector: '[data-cart-subtotal]', block: 'cart-footer' },
      { id: 'cart-drawer', selector: '[data-minimog-gift-wrapping]', block: 'cart-footer' }
    ];
  }

  getSectionDOM(source, selector = '.shopify-section') {
    return new DOMParser().parseFromString(source, 'text/html').querySelector(selector);
  }

  setActiveElement(el) {
    this.activeElement = el;
  }
}
customElements.define('m-cart-drawer', MCartDrawer);

class MCartDrawerItems extends MCartTemplate {
  getSectionsToRender() {
    return [
      { id: 'MinimogCartDrawer', section: 'cart-drawer', selector: '[data-shipping]' },
      { id: 'MinimogCartDrawer', section: 'cart-drawer', selector: '[data-minimog-cart-items]' },
      { id: 'MinimogCartDrawer', section: 'cart-drawer', selector: '[data-minimog-cart-discounts]' },
      { id: 'MinimogCartDrawer', section: 'cart-drawer', selector: '[data-cart-subtotal]' },
      { id: 'MinimogCartDrawer', section: 'cart-drawer', selector: '[data-minimog-gift-wrapping]' }
    ];
  }
}
customElements.define('m-cart-drawer-items', MCartDrawerItems);

/* Bind Minimog pub/sub events after DOM is ready */
document.addEventListener('DOMContentLoaded', __kaelderHookCartEvents);
